import React, { useState, useEffect } from 'react';
import { PageHeader } from '../../components/Layout';
import { api } from '../../services/api';
import {
  HeadlineCards,
  CostBreakdownChart,
  TrendLineChart,
  ProductionChart,
  ProfitabilityTable,
  ReworkStats,
  ScrapStats,
  InsightsPanel,
} from '../../components/analytics';

type TimePeriod = '30' | '90' | '180' | '365' | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

export function CostAnalyticsPage() {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30');
  const [customRange, setCustomRange] = useState<DateRange>({
    startDate: '',
    endDate: '',
  });
  const [trendMetric, setTrendMetric] = useState<'cost' | 'margin' | 'volume'>('cost');
  const [productionView, setProductionView] = useState<'monthly' | 'workstation'>('monthly');

  // Data states
  const [summary, setSummary] = useState<any>(null);
  const [costBreakdown, setCostBreakdown] = useState<any>(null);
  const [trends, setTrends] = useState<any>(null);
  const [productionMetrics, setProductionMetrics] = useState<any>(null);
  const [workstationData, setWorkstationData] = useState<any>(null);
  const [profitability, setProfitability] = useState<{ items: any[]; total: number }>({ items: [], total: 0 });
  const [reworkStats, setReworkStats] = useState<any>(null);
  const [scrapStats, setScrapStats] = useState<any>(null);
  const [insights, setInsights] = useState<any>(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);

  // Sort states
  const [sortBy, setSortBy] = useState('margin_percent');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Permission
  const [canView, setCanView] = useState(false);
  const [permissionChecked, setPermissionChecked] = useState(false);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = async () => {
    try {
      const result = await api.costAccounting.canView();
      setCanView(result.canView);
    } catch (error) {
      console.error('Error checking permission:', error);
      setCanView(false);
    } finally {
      setPermissionChecked(true);
    }
  };

  // Load data when time period changes
  useEffect(() => {
    if (canView) {
      loadAllData();
    }
  }, [timePeriod, customRange, canView]);

  const getDays = () => parseInt(timePeriod === 'custom' ? '30' : timePeriod);
  const getStartDate = () => timePeriod === 'custom' ? customRange.startDate : undefined;
  const getEndDate = () => timePeriod === 'custom' ? customRange.endDate : undefined;

  const loadAllData = async () => {
    if (timePeriod === 'custom' && (!customRange.startDate || !customRange.endDate)) {
      return;
    }

    setLoading(true);
    const days = getDays();
    const startDate = getStartDate();
    const endDate = getEndDate();

    try {
      const [
        summaryData,
        breakdownData,
        trendsData,
        productionData,
        workstationDataResult,
        profitabilityData,
        reworkData,
        scrapData,
        insightsData,
      ] = await Promise.all([
        api.costAccounting.getSummary(days, startDate, endDate),
        api.costAccounting.getCostBreakdown(days, startDate, endDate),
        api.costAccounting.getTrends(days, startDate, endDate),
        api.costAccounting.getProductionMetrics(days, startDate, endDate),
        api.costAccounting.getByWorkstation(days, startDate, endDate),
        api.costAccounting.getProfitability(days, startDate, endDate, sortBy, sortDir),
        api.costAccounting.getReworkStats(days, startDate, endDate),
        api.costAccounting.getScrapStats(days, startDate, endDate),
        api.costAccounting.getInsights(days, startDate, endDate),
      ]);

      setSummary(summaryData);
      setCostBreakdown(breakdownData);
      setTrends(trendsData);
      setProductionMetrics(productionData);
      setWorkstationData(workstationDataResult);
      setProfitability(profitabilityData);
      setReworkStats(reworkData);
      setScrapStats(scrapData);
      setInsights(insightsData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = async (column: string) => {
    const newDir = sortBy === column && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(column);
    setSortDir(newDir);

    setProfitabilityLoading(true);
    try {
      const days = getDays();
      const startDate = getStartDate();
      const endDate = getEndDate();
      const data = await api.costAccounting.getProfitability(days, startDate, endDate, column, newDir);
      setProfitability(data);
    } catch (error) {
      console.error('Error sorting profitability:', error);
    } finally {
      setProfitabilityLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const days = getDays();
      const startDate = getStartDate();
      const endDate = getEndDate();
      await api.costAccounting.exportCsv(days, startDate, endDate);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV');
    }
  };

  if (!permissionChecked) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You do not have permission to view cost analytics.</p>
          <p className="text-gray-400 text-sm mt-2">Contact an administrator to request access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cost Analytics"
        subtitle="True cost per screen and profitability analysis"
        actions={
          <div className="flex items-center gap-4">
            {/* Time period selector */}
            <div className="flex items-center gap-2">
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="180">Last 180 Days</option>
                <option value="365">Last 365 Days</option>
                <option value="custom">Custom Range</option>
              </select>

              {timePeriod === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customRange.startDate}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={customRange.endDate}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </>
              )}
            </div>

            <button
              onClick={loadAllData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        }
      />

      {/* Headline Cards */}
      <HeadlineCards summary={summary} loading={loading} />

      {/* Charts Row 1: Cost Breakdown + Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CostBreakdownChart data={costBreakdown} loading={loading} />

        <div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Trends Over Time</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setTrendMetric('cost')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    trendMetric === 'cost'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Costs
                </button>
                <button
                  onClick={() => setTrendMetric('margin')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    trendMetric === 'margin'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Margin
                </button>
                <button
                  onClick={() => setTrendMetric('volume')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    trendMetric === 'volume'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Volume
                </button>
              </div>
            </div>
            <TrendLineChart data={trends} loading={loading} metric={trendMetric} />
          </div>
        </div>
      </div>

      {/* Charts Row 2: Production + Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Production Analysis</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setProductionView('monthly')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    productionView === 'monthly'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setProductionView('workstation')}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    productionView === 'workstation'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Workstations
                </button>
              </div>
            </div>
            <ProductionChart
              productionData={productionMetrics}
              workstationData={workstationData}
              loading={loading}
              view={productionView}
            />
          </div>
        </div>
        <InsightsPanel insights={insights} loading={loading} />
      </div>

      {/* Quality Row: Rework + Scrap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ReworkStats data={reworkStats} loading={loading} />
        <ScrapStats data={scrapStats} loading={loading} />
      </div>

      {/* Profitability Table */}
      <ProfitabilityTable
        items={profitability.items}
        total={profitability.total}
        loading={loading || profitabilityLoading}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        onExportCsv={handleExportCsv}
      />
    </div>
  );
}

export default CostAnalyticsPage;
